import { Box, Button, Checkbox, FormControl, TextField, Autocomplete } from "@mui/material";
import {
  DatePicker,
  MultiViewCalendar,
  ToggleButton,
  ToggleButtonProps,
} from "@progress/kendo-react-dateinputs";
import { LocalizationProvider, IntlProvider } from "@progress/kendo-react-intl";
import { theme } from "components";
import { CalendarIcon } from "components/common-icons/common-icons";
import { useFocusStyles } from "hooks/focusBorder";
import { useTranslation } from "react-i18next";
import { useStyles } from "./enrol-filter-styles";
import { useEffect, useState } from "react";
import { DATE_PICKER_FORMAT, HELP_TEXT, DDL_HELP_TEXT } from "utils/constants";

interface EnrolFilterProps {
  handleFilter: (fromDate: Date, toDate: Date, selectedLocations: string[]) => void;
  handleReset: () => void;
  availableLocations: string[];
}

export const EnrolFilter = ({
  handleFilter,
  handleReset,
  availableLocations,
}: EnrolFilterProps) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const fromDateLabelId = "fromDate-label";
  const toDateLabelId = "toDate-label";

  const focusClass = useFocusStyles();
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const filterSelected = !!fromDate || !!toDate || selectedLocations.length > 0;
  const minDate = new Date();
  const compareDate = new Date();
  compareDate.setDate(compareDate.getDate() - 1);

  const handleFromDateChange = (e: any) => {
    const selectedDate = new Date(e.target.value).toISOString().split("T")[0];
    if (selectedDate >= compareDate.toISOString().split("T")[0]) {
      setFromDate(e.target.value);
      document.querySelector("#fromDate")?.setAttribute("aria-label", e.value?.toDateString());
    } else {
      setFromDate(null);
    }
  };

  const handleToDateChange = (e: any) => {
    const selectedDate = new Date(e.target.value).toISOString().split("T")[0];
    if (selectedDate >= compareDate.toISOString().split("T")[0]) {
      setToDate(e.target.value);
      document.querySelector("#toDate")?.setAttribute("aria-label", e.value?.toDateString());
    } else {
      setToDate(null);
    }
  };

  const [lastAppliedFilters, setLastAppliedFilters] = useState<{
    fromDate: Date | null;
    toDate: Date | null;
    locations: string[];
  }>({ fromDate: null, toDate: null, locations: [] });

  const filtersChanged =
    fromDate !== lastAppliedFilters.fromDate ||
    toDate !== lastAppliedFilters.toDate ||
    JSON.stringify(selectedLocations.sort()) !==
      JSON.stringify(lastAppliedFilters.locations.sort());

  const reset = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedLocations([]);
    setLastAppliedFilters({ fromDate: null, toDate: null, locations: [] });
    handleReset();
  };

  useEffect(() => {
    reset();
  }, [availableLocations]);

  return (
    <Box className={classes.root}>
      <LocalizationProvider language={"en-US"}>
        <IntlProvider locale={"en"}>
          <Box className={classes.filtersAndActions}>
            <Box className={classes.filterElementDiv}>
              <Box className={classes.inlineLabelWrapper}>
                <label id={fromDateLabelId} htmlFor="fromDate" className={classes.filterLabel}>
                  {t("from")}
                </label>
                <DatePicker
                  id="fromDate"
                  ariaLabelledBy={fromDateLabelId}
                  title={HELP_TEXT}
                  value={fromDate}
                  formatPlaceholder="formatPattern"
                  format={DATE_PICKER_FORMAT}
                  placeholder={DATE_PICKER_FORMAT}
                  calendar={(props: any) => (
                    <MultiViewCalendar
                      min={minDate}
                      views={1}
                      ref={props._ref}
                      value={props.value}
                      onChange={props.onChange}
                    />
                  )}
                  toggleButton={(props: ToggleButtonProps) => (
                    <ToggleButton {...props}>
                      <CalendarIcon
                        fill={theme.palette.common.black}
                        className={classes.calendarIcon}
                      />
                    </ToggleButton>
                  )}
                  className={classes.filterElement}
                  popupSettings={{ popupClass: classes.calendarPopup }}
                  onChange={handleFromDateChange}
                />
              </Box>
            </Box>
            <Box className={classes.filterElementDiv}>
              <Box className={classes.inlineLabelWrapper}>
                <label id={toDateLabelId} htmlFor="toDate" className={classes.filterLabel}>
                  {t("to")}
                </label>
                <DatePicker
                  id="toDate"
                  ariaLabelledBy={toDateLabelId}
                  title={HELP_TEXT}
                  value={toDate}
                  formatPlaceholder="formatPattern"
                  format={DATE_PICKER_FORMAT}
                  placeholder={DATE_PICKER_FORMAT}
                  calendar={(props: any) => (
                    <MultiViewCalendar
                      min={minDate}
                      views={1}
                      ref={props._ref}
                      value={props.value}
                      onChange={props.onChange}
                    />
                  )}
                  toggleButton={(props: ToggleButtonProps) => (
                    <ToggleButton {...props}>
                      <CalendarIcon
                        fill={theme.palette.common.black}
                        className={classes.calendarIcon}
                      />
                    </ToggleButton>
                  )}
                  className={classes.filterElement}
                  popupSettings={{ popupClass: classes.calendarPopup }}
                  onChange={handleToDateChange}
                />
              </Box>
            </Box>
            <Box className={classes.filterElementDiv}>
              <Box className={classes.dropdownDiv}>
                <label htmlFor="location" className={`${classes.filterLabel} ${classes.noWrap}`}>
                  {t("location")}
                </label>
                <FormControl
                  className={`${classes.filterElement} ${classes.filterPlaceholder}`}
                  fullWidth
                >
                  <Autocomplete
                    id="location"
                    multiple
                    disableCloseOnSelect
                    options={availableLocations}
                    value={selectedLocations}
                    onChange={(_, newValue) => setSelectedLocations(newValue)}
                    getOptionLabel={(option) => option}
                    renderOption={(props, option, { selected }) => (
                      <li {...props} role="option" aria-selected={selected}>
                        <Checkbox checked={selected} className={classes.filterCheckbox} />
                        {option}
                      </li>
                    )}
                    renderTags={() => null}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        title={DDL_HELP_TEXT}
                        placeholder={selectedLocations.join(", ")}
                        fullWidth
                        className={classes.filterElement}
                        slotProps={{
                          htmlInput: {
                            ...params.inputProps,
                            "aria-label": t("location") as string,
                          },
                        }}
                      />
                    )}
                    ListboxProps={{
                      className: "dropDownListBox",
                    }}
                    className={classes.autoComplete}
                  />
                </FormControl>
              </Box>
            </Box>
            <Box className={classes.buttonContainer}>
              <Button
                disabled={!filtersChanged}
                onClick={() => {
                  handleFilter(fromDate, toDate, selectedLocations);
                  setLastAppliedFilters({
                    fromDate,
                    toDate,
                    locations: [...selectedLocations],
                  });
                }}
                className={`${classes.filterButton} ${focusClass.secondaryHover} ${focusClass.focusItem}`}
              >
                {t("apply")}
              </Button>
              <Button
                disabled={!filterSelected && !filtersChanged}
                onClick={reset}
                className={`${classes.filterButton} ${focusClass.secondaryHover} ${focusClass.focusItem}`}
              >
                {t("reset")}
              </Button>
            </Box>
          </Box>
        </IntlProvider>
      </LocalizationProvider>
    </Box>
  );
};
